import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	BestTimeToPostCell,
	GetSocialListeningMyBrandAnalyticsBestTimeToPostService
} from '../../../../../../services/get-social-listening-my-brand-analytics-best-time-to-post.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

interface HeatRect {
	id: string;
	x: number;
	y: number;
	w: number;
	h: number;
	opacity: number;
}

interface AxisLabel {
	id: string;
	x: number;
	y: number;
	text: string;
}

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Figma frame renders the 6am → 11pm window (18 columns).
const DISPLAY_HOURS = [
	6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23
];

// SVG heatmap geometry (viewBox units).
const VB_W = 1000;
const VB_H = 360;
const PAD_LEFT = 42;
const PAD_RIGHT = 6;
const PAD_TOP = 6;
const PAD_BOTTOM = 28;
const GAP = 5;
const CELL_RX = 3;
const COLS = DISPLAY_HOURS.length;
const ROWS = WEEK_DAYS.length;
const GRID_W = VB_W - PAD_LEFT - PAD_RIGHT;
const GRID_H = VB_H - PAD_TOP - PAD_BOTTOM;
const CELL_W = (GRID_W - GAP * (COLS - 1)) / COLS;
const CELL_H = (GRID_H - GAP * (ROWS - 1)) / ROWS;

// Lightest visible tint so zero-value cells still read as part of the grid.
const MIN_OPACITY = 0.12;

function parseHour(label: string): number | null {
	const match = label.trim().match(/^(\d{1,2})\s*(AM|PM)$/i);
	if (!match) return null;
	const raw = parseInt(match[1], 10);
	const meridiem = match[2].toUpperCase();
	if (meridiem === 'AM') return raw === 12 ? 0 : raw;
	return raw === 12 ? 12 : raw + 12;
}

// Column axis label — single-letter meridiem, e.g. 6 → "6a", 19 → "7p".
function hourColLabel(hour: number): string {
	const meridiem = hour < 12 ? 'a' : 'p';
	const twelve = hour % 12 === 0 ? 12 : hour % 12;
	return `${twelve}${meridiem}`;
}

// KPI callout label — full meridiem, e.g. 19 → "7pm".
function hourFullLabel(hour: number): string {
	const meridiem = hour < 12 ? 'am' : 'pm';
	const twelve = hour % 12 === 0 ? 12 : hour % 12;
	return `${twelve}${meridiem}`;
}

@Component({
	standalone: true,
	selector: 'app-best-time-to-post-slide',
	templateUrl: './best-time-to-post-slide.component.html',
	styleUrls: ['./best-time-to-post-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class BestTimeToPostSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	private destroy$ = new Subject<void>();
	private cachedCells: BestTimeToPostCell[] | null = null;
	private cacheKey: string | null = null;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	readonly accentColor = '#2872e1';
	readonly heatmapViewBox = `0 0 ${VB_W} ${VB_H}`;
	readonly cellRx = CELL_RX;

	isEmpty = true;
	peakLabel = '';
	heatmapCells: HeatRect[] = [];
	dayLabels: AxisLabel[] = [];
	hourLabels: AxisLabel[] = [];
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'best_time_to_post',
		null,
		null
	);

	constructor(
		private analyticsService: GetSocialListeningMyBrandAnalyticsBestTimeToPostService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedCells && this.cacheKey === currentCacheKey) {
			this.applyCells(this.cachedCells);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedCells = data;
				this.cacheKey = currentCacheKey;
				this.applyCells(data);
			});
	}

	private applyCells(data: BestTimeToPostCell[]): void {
		const rawSlide = this.buildSlide(data);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	trackCell(_index: number, cell: HeatRect): string {
		return cell.id;
	}

	trackLabel(_index: number, label: AxisLabel): string {
		return label.id;
	}

	private buildSlide(cells: BestTimeToPostCell[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'best_time_to_post',
			title: this.item.title || 'Best Time to Post',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Best Time to Post',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_heatmap_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'best_time_to_post_heatmap',
						columns: [
							{ key: 'day', label: 'Day' },
							{ key: 'hour', label: 'Hour' },
							{ key: 'value', label: 'Value' }
						],
						rows: cells.map(c => ({
							day: c.day,
							hour: c.hour,
							value: String(c.value)
						}))
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
		const heatmapWidget =
			(slide.widgets.find(
				widget =>
					widget.kind === 'table' &&
					(widget as TableWidget).content.dataSourceKey ===
						'best_time_to_post_heatmap' &&
					!hidden.has(widget.id)
			) as TableWidget | undefined) ?? null;

		this.dateLabel =
			this.getTitleWidget('date_range', hidden, slide)?.content.text ??
			slide.dateRange?.label ??
			'';
		this.summaryText =
			this.getSummaryWidget(hidden, slide)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget('title', hidden, slide)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget('title', hidden, slide)?.content.color ??
			'#2872e1';

		const rawCells: BestTimeToPostCell[] = (
			heatmapWidget?.content.rows ?? []
		).map(row => ({
			day: row['day'] ?? '',
			hour: row['hour'] ?? '',
			value: Number(row['value'] ?? 0)
		}));
		this.buildHeatmapView(rawCells);

		// Every cell, across the whole day rather than the 6am-11pm window
		// the grid is designed around: redrawn as a line or a table, the
		// hours the design leaves off are numbers like any other.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				rows: rawCells.flatMap(cell => {
					const hour = parseHour(cell.hour);
					return hour === null
						? []
						: [{ day: cell.day, hour, value: cell.value }];
				})
			})
		);
	}

	private buildHeatmapView(cells: BestTimeToPostCell[]): void {
		// Index engagement by "day|hourInt" for the displayed window.
		const byKey = new Map<string, number>();
		for (const cell of cells) {
			const hour = parseHour(cell.hour);
			if (hour === null) continue;
			byKey.set(`${cell.day}|${hour}`, cell.value);
		}

		let max = 0;
		let peakValue = -1;
		let peakDay = '';
		let peakHour = -1;
		for (const day of WEEK_DAYS) {
			for (const hour of DISPLAY_HOURS) {
				const value = byKey.get(`${day}|${hour}`) ?? 0;
				if (value > max) max = value;
				if (value > peakValue) {
					peakValue = value;
					peakDay = day;
					peakHour = hour;
				}
			}
		}

		this.isEmpty = max <= 0;
		if (this.isEmpty) {
			this.peakLabel = '';
			this.heatmapCells = [];
			this.dayLabels = [];
			this.hourLabels = [];
			return;
		}

		this.peakLabel = `${peakDay} ${hourFullLabel(peakHour)}`;

		const rects: HeatRect[] = [];
		const dayLabels: AxisLabel[] = [];
		const hourLabels: AxisLabel[] = [];

		WEEK_DAYS.forEach((day, row) => {
			const y = PAD_TOP + row * (CELL_H + GAP);
			dayLabels.push({
				id: `d-${day}`,
				x: PAD_LEFT - 10,
				y: y + CELL_H / 2,
				text: day
			});
			DISPLAY_HOURS.forEach((hour, col) => {
				const value = byKey.get(`${day}|${hour}`) ?? 0;
				const ratio = max > 0 ? Math.min(value / max, 1) : 0;
				rects.push({
					id: `${day}-${hour}`,
					x: PAD_LEFT + col * (CELL_W + GAP),
					y,
					w: CELL_W,
					h: CELL_H,
					opacity: MIN_OPACITY + (1 - MIN_OPACITY) * ratio
				});
			});
		});

		DISPLAY_HOURS.forEach((hour, col) => {
			hourLabels.push({
				id: `h-${hour}`,
				x: PAD_LEFT + col * (CELL_W + GAP) + CELL_W / 2,
				y: VB_H - PAD_BOTTOM / 2,
				text: hourColLabel(hour)
			});
		});

		this.heatmapCells = rects;
		this.dayLabels = dayLabels;
		this.hourLabels = hourLabels;
	}

	private getTitleWidget(
		kind: TitleWidget['kind'],
		hidden: Set<string>,
		slide: PreviewSlide
	): TitleWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === kind && !hidden.has(widget.id)
		) as TitleWidget | undefined;
	}

	private getSummaryWidget(
		hidden: Set<string>,
		slide: PreviewSlide
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;
	}
}
